const React = require('react');
const { useCallback, useState } = React;
const T = require('prop-types');
const { default: Styled } = require('styled-components');
const { connect } = require('react-redux');
const { push: Push, replace: Replace } = require('connected-react-router');
const { context } = require('app-context');

const { default: Box } = require('@mui/material/Box');
const { default: Typography } = require('@mui/material/Typography');
const { default: Avatar } = require('@mui/material/Avatar');
const { default: Button } = require('@mui/material/Button');
const { default: Chip } = require('@mui/material/Chip');
const { default: Divider } = require('@mui/material/Divider');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: Tooltip } = require('@mui/material/Tooltip');
const { default: Accordion } = require('@mui/material/Accordion');
const { default: AccordionSummary } = require('@mui/material/AccordionSummary');
const { default: AccordionDetails } = require('@mui/material/AccordionDetails');
const { default: ChevronLeftIcon } = require('@mui/icons-material/ChevronLeft');
const { default: ChevronRightIcon } = require('@mui/icons-material/ChevronRight');
const { default: ContentCopyIcon } = require('@mui/icons-material/ContentCopy');
const { default: ExpandMoreIcon } = require('@mui/icons-material/ExpandMore');
const { default: OpenInNewIcon } = require('@mui/icons-material/OpenInNew');
const { getDisplayImageUrl: getOfferMediaUrl } = require('utils/image');
const InternalAppLink = require('utils/internalAppLink');
const ScrollPortal = require('components/ScrollPortal');
const YouTubePlayer = require('components/YouTubePlayer');

const selectors = context.selectors.all;
const actions = context.actions;
const redux = context.redux.hooks;
const api = context.api.nearpeer;

const internals = {};

const OfferDetailPage = function OfferDetailPage(props) {

    const { offer, offers, isLoadingOffers, history, location, onNavigateToOffer, onNavigateInApp } = props;

    const isFromList = new URLSearchParams(location?.search).get('fromList') === 'true';
    const currentIndex = isFromList && offers ? offers.findIndex((o) => o.id === offer?.id) : -1;
    const showNavigation = isFromList && offers?.length > 1 && currentIndex >= 0;
    const prevOffer = showNavigation ? offers[(currentIndex - 1 + offers.length) % offers.length] : null;
    const nextOffer = showNavigation ? offers[(currentIndex + 1) % offers.length] : null;

    const getMediaSrc = (url) => getOfferMediaUrl(url) || null;
    const heroImageSrc = getMediaSrc(offer?.heroImageUrl);
    const heroVideoSrc = getMediaSrc(offer?.heroVideoUrl);
    const youtubeEmbedUrl = YouTubePlayer.getEmbedUrl(offer?.heroVideoUrl);
    const isHeroVideo = Boolean(youtubeEmbedUrl || (offer?.mediaType === 'video' && offer?.heroVideoUrl));
    const [loadedYoutubeOfferKeys, setLoadedYoutubeOfferKeys] = useState(() => new Set());
    const currentYoutubeOfferKey = youtubeEmbedUrl ? `${offer.id}:${offer.heroVideoUrl}` : null;
    const canPreloadAdjacentYoutubeOffers = !currentYoutubeOfferKey || loadedYoutubeOfferKeys.has(currentYoutubeOfferKey);
    const youtubeOffersToMount = [
        offer,
        ...(showNavigation && canPreloadAdjacentYoutubeOffers ? [prevOffer, nextOffer] : [])
    ].filter((candidate, index, candidates) => (
        candidate &&
        YouTubePlayer.getEmbedUrl(candidate.heroVideoUrl) &&
        candidates.findIndex((item) => item?.id === candidate.id) === index
    ));
    const [heroImageLayout, setHeroImageLayout] = useState({ src: null, aspectRatio: null });
    const heroImageAspectRatio = heroImageLayout.src === heroImageSrc ? heroImageLayout.aspectRatio : null;

    const handleHeroImageLoad = useCallback((event) => {

        const { naturalWidth, naturalHeight } = event.currentTarget;
        if (!naturalWidth || !naturalHeight) {
            return;
        }

        const aspectRatio = naturalWidth / naturalHeight;
        setHeroImageLayout({
            src: heroImageSrc,
            aspectRatio: aspectRatio > 16 / 9 ? `${naturalWidth} / ${naturalHeight}` : null
        });
    }, [heroImageSrc]);

    const handleYoutubeOfferLoad = useCallback((youtubeOffer) => {

        const key = `${youtubeOffer.id}:${youtubeOffer.heroVideoUrl}`;

        setLoadedYoutubeOfferKeys((loadedKeys) => {

            if (loadedKeys.has(key)) {
                return loadedKeys;
            }

            return new Set([...loadedKeys, key]);
        });
    }, []);

    const now = new Date();
    const offersLoaded = !isLoadingOffers;
    const isNotTargeted = offersLoaded && !!offer && !offers?.some((o) => o.id === offer.id);
    const isUnavailable = !offer ||
        offer.status === 'disabled' ||
        (offer.expirationDate && new Date(offer.expirationDate) < now) ||
        (offer.endDate && new Date(offer.endDate) < now) ||
        isNotTargeted;

    if (isUnavailable) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                <Typography color='text.secondary'>Offer not found or has expired.</Typography>
                <Button variant='text' onClick={() => history.push('/app/offers')}>Back to offers</Button>
            </Box>
        );
    }

    const {
        BrandBar,
        HeroMedia,
        CouponBox,
        CtaBar,
        CtaButton,
        NavButton,
        YouTubeLayer
    } = internals;

    const expiresText = offer.expirationDate
        ? `Expires ${new Date(offer.expirationDate).toLocaleDateString()}`
        : null;

    const internalPath = InternalAppLink.getInternalAppPath(offer.ctaDeepLink)
        || InternalAppLink.getInternalAppPath(offer.ctaUrl);

    const handleCta = (source) => {

        api.offers.recordClick(offer.id, source);

        if (internalPath) {
            onNavigateInApp(internalPath);
            return;
        }

        internals.openExternalOfferUrl(offer.ctaUrl);
    };

    const handleCopy = () => {

        navigator.clipboard?.writeText(offer.couponCode);
        redux.dispatch(actions.alerts.notification.push({ message: 'Copied to clipboard' }));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {showNavigation && (
                <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, pt: 1, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Tooltip title={prevOffer.brandName} placement='bottom' arrow>
                        <NavButton
                            size='small'
                            variant='outlined'
                            startIcon={<ChevronLeftIcon />}
                            onClick={() => onNavigateToOffer(prevOffer.id)}
                        >
                            Prev
                        </NavButton>
                    </Tooltip>
                    <Typography
                        variant='body1'
                        sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            fontWeight: 600,
                            userSelect: 'none',
                            textAlign: 'center',
                            pointerEvents: 'none'
                        }}
                    >
                        Offer #{currentIndex + 1} of {offers.length}
                    </Typography>
                    <Tooltip title={nextOffer.brandName} placement='bottom' arrow>
                        <NavButton
                            size='small'
                            variant='outlined'
                            endIcon={<ChevronRightIcon />}
                            onClick={() => onNavigateToOffer(nextOffer.id)}
                        >
                            Next
                        </NavButton>
                    </Tooltip>
                </Box>
            )}

            <Box sx={{ pb: 2 }}>
                <BrandBar>
                    <Avatar
                        src={getMediaSrc(offer.brandLogoUrl)}
                        alt={offer.brandName}
                        variant='rounded'
                        sx={{
                            width: 56,
                            height: 56,
                            mr: 2,
                            flexShrink: 0,
                            bgcolor: 'background.paper',
                            '& img': {
                                objectFit: 'contain'
                            }
                        }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                            {offer.brandName}
                        </Typography>
                        {offer.tagText && (
                            <Chip
                                label={offer.tagText}
                                size='small'
                                onClick={() => handleCta('app_tag')}
                                sx={{
                                    mt: 0.5,
                                    display: 'inline-flex',
                                    width: 'fit-content',
                                    maxWidth: '100%',
                                    height: 'auto',
                                    alignItems: 'center',
                                    backgroundColor: offer.tagColor || undefined,
                                    color: offer.tagTextColor || (offer.tagColor ? '#fff' : undefined),
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: offer.tagColor || undefined,
                                        opacity: 0.85
                                    },
                                    '& .MuiChip-label': {
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        py: 0.25,
                                        textAlign: 'center'
                                    }
                                }}
                            />
                        )}
                    </Box>
                </BrandBar>

                <Box sx={{ px: 2, pt: 2 }}>
                    <Typography variant='h5' sx={{ fontWeight: 700, lineHeight: 1.3, mb: 1 }}>
                        {offer.title}
                    </Typography>
                </Box>

                {(offer.heroImageUrl || offer.heroVideoUrl) && (
                    <>
                        <HeroMedia
                            onClick={youtubeEmbedUrl ? undefined : () => handleCta('app_hero')}
                            $aspectRatio={!isHeroVideo ? heroImageAspectRatio : null}
                        >
                            {youtubeOffersToMount.map((youtubeOffer) => {

                                const isActive = youtubeOffer.id === offer.id;

                                return (
                                    <YouTubeLayer
                                        key={youtubeOffer.id}
                                        $active={isActive}
                                    >
                                        <YouTubePlayer
                                            url={youtubeOffer.heroVideoUrl}
                                            title={youtubeOffer.title}
                                            active={isActive}
                                            playerId={`nearpeer-youtube-player-${youtubeOffer.id}`}
                                            onLoad={() => handleYoutubeOfferLoad(youtubeOffer)}
                                        />
                                    </YouTubeLayer>
                                );
                            })}
                            {!youtubeEmbedUrl && (() => {

                                if (offer.mediaType === 'video' && offer.heroVideoUrl) {
                                    return (
                                        <video
                                            key={offer.id}
                                            src={heroVideoSrc}
                                            autoPlay
                                            loop
                                            muted
                                            playsInline
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    );
                                }

                                return (
                                    <img
                                        key={offer.id}
                                        src={heroImageSrc}
                                        alt={offer.title}
                                        onLoad={handleHeroImageLoad}
                                        style={{ width: '100%', height: '100%', objectFit: 'contain', backgroundColor: 'transparent' }}
                                    />
                                );
                            })()}
                        </HeroMedia>
                    </>
                )}

                <Box sx={{ px: 2, pt: 2 }}>
                    {offer.subtitle && (
                        <Typography variant='body1' sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                            {offer.subtitle}
                        </Typography>
                    )}

                    {offer.couponCode && (
                        <CouponBox>
                            <Typography variant='body2' sx={{ mr: 1, color: 'text.secondary' }}>
                                Coupon code:
                            </Typography>
                            <Typography variant='subtitle1' sx={{ fontWeight: 700, letterSpacing: 2, flex: 1 }}>
                                {offer.couponCode}
                            </Typography>
                            <IconButton size='small' onClick={handleCopy} aria-label='Copy coupon code'>
                                <ContentCopyIcon fontSize='small' />
                            </IconButton>
                        </CouponBox>
                    )}

                    {expiresText && (
                        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 2 }}>
                            {expiresText}
                        </Typography>
                    )}

                    {offer.terms && (
                        <Accordion disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant='body2' sx={{ fontWeight: 600 }}>Terms &amp; Conditions</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'pre-wrap' }}>
                                    {offer.terms}
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </Box>
            </Box>

            <ScrollPortal bottom>
                <CtaBar>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                        <CtaButton
                            variant='contained'
                            fullWidth
                            size='large'
                            endIcon={internalPath ? undefined : <OpenInNewIcon />}
                            onClick={() => handleCta('app_cta')}
                            sx={{
                                bgcolor: (theme) => offer.ctaBgColor || theme.palette.primary.main,
                                color: offer.ctaTextColor || '#000000',
                                '&:hover': {
                                    bgcolor: (theme) => offer.ctaBgColor || theme.palette.primary.main
                                }
                            }}
                        >
                            {offer.ctaText}
                        </CtaButton>
                    </Box>
                </CtaBar>
            </ScrollPortal>
        </Box>
    );
};

OfferDetailPage.propTypes = {
    offer: T.object,
    offers: T.array,
    isLoadingOffers: T.bool,
    history: T.object,
    location: T.object,
    onNavigateToOffer: T.func,
    onNavigateInApp: T.func
};

internals.BrandBar = Styled(Box)`
    display: flex;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
`;

internals.HeroMedia = Styled(Box)`
    margin: 16px;
    border-radius: 12px;
    overflow: hidden;
    aspect-ratio: ${({ $aspectRatio }) => $aspectRatio || '16 / 9'};
    background-color: transparent;
    cursor: pointer;
    transition: opacity 0.2s ease;
    position: relative;

    &:hover {
        opacity: 0.9;
    }
`;

internals.YouTubeLayer = Styled.div`
    position: absolute;
    inset: 0;
    z-index: ${({ $active }) => $active ? 1 : 0};
    visibility: ${({ $active }) => $active ? 'visible' : 'hidden'};
    pointer-events: ${({ $active }) => $active ? 'auto' : 'none'};
`;

internals.CouponBox = Styled(Box)`
    display: flex;
    align-items: center;
    border: 2px dashed ${({ theme }) => theme.palette.primary.main};
    border-radius: 8px;
    padding: 8px 12px;
    margin-bottom: 12px;
    background-color: ${({ theme }) => theme.palette.primary.main}11;
`;

internals.CtaBar = Styled(Box)`
    width: 100%;
    background-color: ${({ theme }) => theme.palette.background.paper};
`;

internals.CtaButton = Styled(Button)`
    border-radius: 24px !important;
    font-weight: 700 !important;
    letter-spacing: 0.5px !important;
    transition: none !important;
`;

internals.NavButton = Styled(Button)`
    min-width: 0 !important;
    flex-shrink: 1;
    border-radius: 16px !important;
    text-transform: none !important;
    font-weight: 600 !important;
    border-color: ${({ theme }) => theme.palette.primary.main}80 !important;
    background-color: ${({ theme }) => theme.palette.primary.main}0F !important;

    &:hover {
        border-color: ${({ theme }) => theme.palette.primary.main} !important;
        background-color: ${({ theme }) => theme.palette.primary.main}1F !important;
    }

    & .MuiButton-startIcon,
    & .MuiButton-endIcon {
        flex-shrink: 0;
    }
`;

const ConnectedOfferDetailPage = connect(
    (state, ownProps) => {

        const offerId = Number(ownProps.match?.params?.id);

        return {
            offer: selectors.getOffer(state, offerId),
            offers: selectors.getOffers(state),
            isLoadingOffers: selectors.getIsLoadingOffers(state)
        };
    },
    (dispatch) => ({
        onNavigateToOffer: (offerId) => dispatch(Replace(`/app/offers/${offerId}?fromList=true`)),
        onNavigateInApp: (path) => dispatch(Push(path))
    })
)(OfferDetailPage);

internals.openExternalOfferUrl = InternalAppLink.openExternalUrl;

module.exports = ConnectedOfferDetailPage;
module.exports.Component = OfferDetailPage;
module.exports.internals = internals;
